import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Button } from "@/components/flexkit/Button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { timeAgo, formatShortDate } from "@/format/time";
import { type PlainServiceAccountToken } from "@/queries/service-account-token-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { useMemo, useState, type FC } from "react";
import { ServiceAccountDeleteTokenModal } from "@/components/settings/organization/service-accounts/ServiceAccountDeleteTokenModal";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    tokens: PlainServiceAccountToken[];
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
};

export const ServiceAccountTokensTable: FC<Props> = ({ tokens, serviceAccountId, headers }) => {
    const [tokenToDelete, setTokenToDelete] = useState<PlainServiceAccountToken | null>(null);

    const sorted = useMemo(() => {
        return [...tokens].sort((a, b) => {
            const aDate = a.createdAt ? new Timestamp(a.createdAt).toDate() : new Date(0);
            const bDate = b.createdAt ? new Timestamp(b.createdAt).toDate() : new Date(0);
            return bDate.getTime() - aDate.getTime();
        });
    }, [tokens]);

    return (
        <>
            <Table data-testid="service-account-tokens-table">
                <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                    <TableRow className="h-8 text-base text-content-strong">
                        <TableHead className="pl-3">Description</TableHead>
                        <TableHead className="pl-3">Created</TableHead>
                        <TableHead className="pl-3">Expires</TableHead>
                        <TableHead className="pl-3">Last used</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="border-t border-border-light text-base">
                    {sorted.map((token) => (
                        <ServiceAccountTokenRow
                            key={token.id}
                            token={token}
                            onDelete={(token) => setTokenToDelete(token)}
                        />
                    ))}
                </TableBody>
            </Table>

            {tokenToDelete && (
                <ServiceAccountDeleteTokenModal
                    token={tokenToDelete}
                    serviceAccountId={serviceAccountId}
                    headers={headers}
                    onClose={() => setTokenToDelete(null)}
                />
            )}
        </>
    );
};

const ServiceAccountTokenRow: FC<{
    token: PlainServiceAccountToken;
    onDelete: (token: PlainServiceAccountToken) => void;
}> = ({ token, onDelete }) => {
    const createdDate = useMemo(() => {
        if (!token.createdAt) {
            return;
        }
        return new Timestamp(token.createdAt).toDate();
    }, [token.createdAt]);

    const expiresDate = useMemo(() => {
        if (!token.expiresAt) {
            return;
        }
        return new Timestamp(token.expiresAt).toDate();
    }, [token.expiresAt]);

    const lastUsedDate = useMemo(() => {
        if (!token.lastUsed) {
            return;
        }
        return new Timestamp(token.lastUsed).toDate();
    }, [token.lastUsed]);

    const isExpired = expiresDate && expiresDate < new Date();

    return (
        <TableRow className="h-16 border-border-light" data-testid={token.id}>
            <TableCell>
                <Text className="truncate text-base font-medium text-content-primary">
                    {token.description || "Unnamed token"}
                </Text>
            </TableCell>
            <TableCell>
                {createdDate ? (
                    <Text className="truncate text-base" title={createdDate.toISOString()}>
                        {timeAgo(createdDate)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">--</span>
                )}
            </TableCell>
            <TableCell>
                {expiresDate ? (
                    <Text
                        className={`truncate text-base ${isExpired ? "text-content-negative" : ""}`}
                        title={expiresDate.toISOString()}
                    >
                        {isExpired ? "Expired" : formatShortDate(expiresDate, true)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">Never</span>
                )}
            </TableCell>
            <TableCell>
                {lastUsedDate ? (
                    <Text className="truncate text-base" title={lastUsedDate.toISOString()}>
                        {timeAgo(lastUsedDate)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">Never</span>
                )}
            </TableCell>
            <TableCell className="w-8">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        data-testid={`delete-service-account-token-${token.id}-button`}
                        LeadingIcon={IconTrash}
                        variant="ghost"
                        size="sm"
                        className="border-0 px-1"
                        onClick={() => onDelete(token)}
                        aria-label="Delete token"
                        data-tracking-id="delete-token-service-account-tokens-table"
                    />
                </div>
            </TableCell>
        </TableRow>
    );
};
